import React, { useState, useId, useEffect } from 'react';
import { ChevronDown, Search, HelpCircle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Helmet } from '../utils/helmet';
import { ToolKey } from '../types';

export interface FAQItem {
  id: string;
  category?: string;
  question: string;
  answer: string;
}

export interface ToolFaqConfig {
  eyebrow: string;
  title: string;
  description: string;
  ctaText: string;
  items: FAQItem[];
}

export const WEIGHTED_COURSE_FAQS: FAQItem[] = [
  {
    id: 'weighted-calc-weighted-average',
    category: 'weighted',
    question: 'How does a grade calculator weighted average work?',
    answer:
      'Each category percentage is multiplied by its syllabus weight and the products are added. With homework 90% at 30%, quizzes 80% at 20% and exams 70% at 50%, the weighted average is 27 + 16 + 35 = 78%.',
  },
  {
    id: 'weighted-weight-grades',
    category: 'weighted',
    question: 'How do I weight grades when categories count for different amounts?',
    answer:
      'Write each weight as a decimal, multiply it by the category percentage and add the results. The weights should total 1.00; if they total less because some work is still ungraded, divide by the weights used so far.',
  },
  {
    id: 'weighted-mark-calculator',
    category: 'weighted',
    question: 'Is there a weighted mark calculator for scores out of different totals?',
    answer:
      'Yes. Each assessment takes its own points earned and points possible, so a 45 out of 50 and a 18 out of 20 can sit in the same list. The page turns each into a percentage before applying the weight.',
  },
  {
    id: 'weighted-average-for-grades',
    category: 'weighted',
    question: 'What is the formula for a weighted average for grades?',
    answer:
      'Weighted average = (score₁ × weight₁ + score₂ × weight₂ + …) ÷ total of the weights used. A 92 at 40% and an 80 at 60% give (36.8 + 48) ÷ 1.00 = 84.8%.',
  },
  {
    id: 'weighted-weightage-marks',
    category: 'weighted',
    question: 'Can this work as a weightage marks calculator?',
    answer:
      'Yes. Weightage is the share a component carries in the final mark, so a paper with 30% weightage and a score of 70% contributes 21 marks to the course total. Enter the weightage in the weight column and the contribution is calculated.',
  },
  {
    id: 'weighted-how-to-calculate',
    category: 'weighted',
    question: 'How do I calculate weighted grade totals when some scores are missing?',
    answer:
      'To calculate weighted grade results with missing scores, include only the categories that have a mark, add their weighted points and divide by the sum of those weights. The result is the current grade, and the final exam calculator shows what the rest of the course needs.',
  },
  {
    id: 'weighted-free-calculator',
    category: 'weighted',
    question: 'Is there a calculator for weighted grades that is free to use?',
    answer:
      'Yes. The tool is free, works on phones and computers, and needs no account. It also functions as a calculator with weighted grades and a letter result, so the same screen gives the percentage, the letter and the target score.',
  },
  {
    id: 'weighted-test-calculator',
    category: 'weighted',
    question: 'Can a weighted test calculator handle a midterm worth 30%?',
    answer:
      'Yes. Add the midterm with a weight of 30 and its score, then add the other categories so the weights total 100. A 78% midterm adds 23.4 points to the course grade, which is a weighted score calculation like any other.',
  },
  {
    id: 'weighted-online-grade-calculator',
    category: 'weighted',
    question: 'What is an online grade calculator for a whole course?',
    answer:
      'It is a tool that combines every graded item into one course percentage. This one is built for weighted syllabi and reports the letter as well. To calculate grade totals for classes graded by total points, switch to Points mode.',
  },
  {
    id: 'weighted-work-out-grade',
    category: 'weighted',
    question: 'How to work out grade percentages when weights differ between categories?',
    answer:
      'Convert each category to a percentage, multiply by its weight and add everything up. That is how to work out grade results for any weighted syllabus, and the calculator shows each category’s contribution next to the total.',
  },
  {
    id: 'weighted-syllabus-formula',
    category: 'weighted',
    question: 'What is the exact formula for a weighted course grade?',
    answer:
      'A weighted course grade is computed by multiplying each syllabus category score percentage by its assigned syllabus weight, summing those weighted products, and dividing by the sum of active weights: Weighted Grade (%) = Σ(Category Score % × Category Weight) ÷ Σ(Active Category Weights).',
  },
  {
    id: 'final-exam-target-equation',
    category: 'exams',
    question: 'How does the Final Exam Target Grade Calculator determine my required exam score?',
    answer:
      'The Final Exam Target Simulator uses the algebraic formula: Required Final Score = (Target Course Grade − (Current Average × (1 − Final Weight))) ÷ Final Weight. For example, with an 88% current average, a 90% target grade, and a 25% final exam weight, you need (90 − (88 × 0.75)) ÷ 0.25 = 96.0% on the final exam.',
  },
  {
    id: 'incomplete-syllabus-weights',
    category: 'weighted',
    question: 'What happens if my entered category weights add up to less than 100% mid-semester?',
    answer:
      'When your entered category weights sum to less than 100% (such as 75% before taking a 25% final exam), the calculator automatically normalizes your current standing by dividing your earned weighted points by 75% rather than 100%, giving you an accurate mid-semester course average.',
  },
  {
    id: 'dropped-lowest-quiz-score',
    category: 'general',
    question: 'How do I account for dropped lowest quiz or homework scores in a weighted class?',
    answer:
      'Remove or exclude the dropped assignment from that category before averaging the remaining scores for that syllabus bucket, or delete the lowest row directly inside the assessment table so only counted coursework is weighted.',
  },
  {
    id: 'extra-credit-weighted-impact',
    category: 'weighted',
    question: 'How does extra credit affect a weighted syllabus vs. a total-points syllabus?',
    answer:
      'In a weighted syllabus, extra credit points added to a low-weight category (such as 10% Homework) only boost that specific category, whereas extra credit applied to a high-weight exam category (such as 40% Midterms) produces four times the increase in your overall course percentage.',
  },
  {
    id: 'weighted-points-or-percent-mode',
    category: 'weighted',
    question: 'Should I use Weighted (%) mode or Points mode for my class?',
    answer:
      'Check the grading section of your syllabus. If it lists category percentages such as Homework 20% and Exams 50%, use Weighted (%) mode. If your instructor adds up raw points and divides by the total possible, use Points mode. When a syllabus mixes both, turn each category’s points into a percentage first, then enter those percentages with their weights.',
  },
  {
    id: 'weighted-average-to-letter',
    category: 'general',
    question: 'What letter grade does a weighted average of 88.4% earn?',
    answer:
      'On the common 10-point scale an 88.4% is a B, because the B range runs from 80% to 89.99%. On the plus/minus scale it is a B+, which starts at 87%. The calculator shows the letter for whichever scale you select, and your syllabus decides which scale your school applies.',
  },
  {
    id: 'weighted-calculation-in-browser',
    category: 'general',
    question: 'Do I need an account to use the weighted grade calculator?',
    answer:
      'No. You can enter your assessments and weights and see your results straight away without signing up. The arithmetic runs in your browser, so you can try as many what-if scores as you like.',
  },
  {
    id: 'weighted-how-system-works',
    category: 'weighted',
    question: 'How does a weighted grading system work?',
    answer:
      'A weighted grading system splits a course into categories such as homework, quizzes, exams and a final, and gives each category a percentage of the course grade. You average the scores inside each category, multiply each average by its weight and add the results. A category with a higher weight moves your grade more than one with a lower weight, even if it contains fewer assignments.',
  },
  {
    id: 'weighted-category-weight-grading',
    category: 'weighted',
    question: 'What is category weight grading?',
    answer:
      'Category weight grading groups assignments into categories and weights each category instead of each assignment. If homework is 20% of the course, all homework together counts for 20% whether there are five assignments or fifty. The calculator applies the weights you enter to each category’s percentage.',
  },
  {
    id: 'weighted-calc-for-a-class',
    category: 'weighted',
    question: 'How do I calculate my weighted grade for a class?',
    answer:
      'Turn each category score into a percentage, multiply it by the category weight written as a decimal, then add the results. If some categories are not graded yet, divide by the total weight used so far. For example, 92% at 20%, 85% at 20%, 78% at 25% and 88% at 15% add up to 68.1 from 80% of the course, which is an 85.1% weighted grade.',
  },
  {
    id: 'weighted-average-grade-meaning',
    category: 'weighted',
    question: 'What is a weighted average grade?',
    answer:
      'A weighted average grade is an average in which some scores count more than others. A 100% on a quiz worth 10% and a 70% on an exam worth 90% give a weighted average of 73%, compared with a simple average of 85%.',
  },
  {
    id: 'weighted-how-to-calculate-by-hand',
    category: 'weighted',
    question: 'How to calculate weighted grades without a calculator?',
    answer:
      'Multiply each category percentage by its weight as a decimal, write down each product, and add the products. Then divide by the total of the weights you used. Keep one extra decimal place until the last step so rounding does not push you over a letter cutoff.',
  },
  {
    id: 'weighted-by-points',
    category: 'weighted',
    question: 'Can I use a weighted grade calculator with points instead of percentages?',
    answer:
      'Yes. In Points mode the calculator adds every point you earned and divides by every point possible, so no weights are needed. Scores of 45 out of 50, 88 out of 100 and 27 out of 30 give 160 of 180 points, or 88.9%. Use Weighted (%) mode when your syllabus gives each category its own percentage.',
  },
  {
    id: 'weighted-80-20-60-40',
    category: 'weighted',
    question: 'How do I calculate a weighted grade with an 80/20 or 60/40 split?',
    answer:
      'Multiply each part by its weight and add. With 80% coursework and a 20% exam, an 88% coursework average and a 72% exam give 0.80 × 88 + 0.20 × 72 = 84.8%. With 60% exams and 40% homework, a 75% exam average and 95% homework give 83%. Type the weights as 80 and 20, or 60 and 40, to get the same result here.',
  },
  {
    id: 'weighted-extra-credit-calculator',
    category: 'weighted',
    question: 'Does the weighted grade calculator allow extra credit?',
    answer:
      'Yes. Enter a score above the Out Of value, such as 105 out of 100, or add a bonus row. The result is not capped at 100%, so you can see the uncapped grade and then apply your instructor’s rule if extra credit is limited.',
  },
  {
    id: 'weighted-vs-unweighted-difference',
    category: 'general',
    question: 'What is the difference between weighted and unweighted grades?',
    answer:
      'In an unweighted grade every point or assignment counts the same. In a weighted grade each category counts for a set share, so an exam worth 40% moves the course grade far more than homework worth 10%. A weighted GPA is a different idea: it adds bonus points for honors or AP classes, and the GPA calculator handles it.',
  },
  {
    id: 'weighted-high-school-ap-honors',
    category: 'general',
    question: 'Can I use it for high school, AP and honors classes?',
    answer:
      'Yes for the class grade. Enter your teacher’s categories and weights to get the course percentage. AP and honors weighting usually changes the GPA scale rather than the class percentage, so take the resulting letter to the GPA calculator for the weighted GPA.',
  },
  {
    id: 'weighted-college-university-credits',
    category: 'general',
    question: 'How do I calculate a weighted grade for college or university modules with credits?',
    answer:
      'Enter each module’s percentage and use its credit value as the weight, so a 40-credit module counts twice as much as a 20-credit module. The calculator divides by the weights you enter, so they do not need to add up to 100. Grading scales and degree classes differ by country, so check your institution’s table for the final label.',
  },
  {
    id: 'weighted-needed-on-final',
    category: 'exams',
    question: 'What do I need on the final exam to keep my weighted grade?',
    answer:
      'Use needed score = (target − current grade × (1 − final weight)) ÷ final weight. With an 82% current grade, a 30% final and an 80% target, that is (80 − 82 × 0.70) ÷ 0.30 = 75.3%. The target grade simulator under your results does this for you.',
  },
  {
    id: 'weighted-excel-google-sheets',
    category: 'general',
    question: 'How do I make a weighted grade calculator in Excel or Google Sheets?',
    answer:
      'List each category percentage in one column and its weight in the next, then use =SUMPRODUCT(B2:B6, C2:C6) / SUM(C2:C6). It works the same in Excel and Google Sheets. This page gives the same answer without building a sheet, and it adds a letter grade and a final exam target.',
  },
  {
    id: 'weighted-canvas-powerschool-blackboard',
    category: 'general',
    question: 'Can I use it with Canvas, Blackboard or PowerSchool grades?',
    answer:
      'Yes, by typing the numbers in. Copy each category’s percentage or points from your school portal and add the weights from your syllabus. The calculator does not connect to school accounts or import anything, which also lets you test what-if scores before real ones are posted.',
  },
  {
    id: 'weighted-semester-full-year',
    category: 'general',
    question: 'How do I calculate a full-year or semester weighted grade?',
    answer:
      'For one semester, enter your categories and weights. For a full year, enter each term’s grade as a row and give each term the weight your school uses, such as 50 and 50. A term that counts for less gets a smaller weight.',
  },
  {
    id: 'weighted-free-app-teachers',
    category: 'general',
    question: 'Is there a free weighted grade calculator app, and can teachers use it?',
    answer:
      'It is free, needs no account and runs in your browser on a phone or computer. You can add it to your home screen from the browser menu, and it keeps working offline after the first visit. Teachers can use it to check one student’s weighted grade; the average grade calculator is the page for a class mean.',
  },
];

/**
 * Per-page FAQ headings so each academic calculator gets its own eyebrow, title, intro and call to action
 * while sharing the exact same FAQ UI as every other tool. Keys match the page slugs.
 */
export type FaqHeading = Partial<Omit<ToolFaqConfig, 'items'>>;

export const PAGE_FAQ_HEADINGS: Record<string, FaqHeading> = {
  'weighted-grade-calculator': {
    eyebrow: 'Weighted Grade FAQs',
    title: 'Weighted Grade Calculator FAQs',
    description:
      'Straight answers on syllabus weights, points vs percentages, extra credit, college credits, the score needed on the final, and Excel or Google Sheets formulas.',
    ctaText: 'Enter your categories, scores and weights above to see your weighted course grade update instantly.',
  },
  'final-exam-grade-calculator': {
    eyebrow: 'Final Exam Target FAQs',
    title: 'Final Exam Grade Calculator FAQs',
    description:
      'Clear answers about the score you need on a final exam, by percent or points: passing, midterms, quarters, replaced or missing finals, curves, GPA and spreadsheet formulas.',
    ctaText: 'Enter your current grade, target grade and final exam weight above to see the exact score you need.',
  },
  'test-grade-calculator': {
    eyebrow: 'Test & Quiz Scoring FAQs',
    title: 'Test Grade Calculator FAQs',
    description:
      'Quick answers on turning points into a percentage, partial credit and extra credit, late penalties, the points needed to pass, dropping the lowest test and how one test affects your overall grade.',
    ctaText: 'Enter your points earned and points possible above to get your test percentage and letter grade.',
  },
  'grade-curve-calculator': {
    eyebrow: 'Curving & Class Stats FAQs',
    title: 'Grade Curve Calculator FAQs',
    description:
      'Answers on the four curving methods, class mean and median, capping at 100 and how a curve changes each student’s letter grade.',
    ctaText: 'Paste your class scores above and compare all four curving methods side by side.',
  },
  'letter-grade-calculator': {
    eyebrow: 'Grade Scale FAQs',
    title: 'Letter Grade Calculator FAQs',
    description:
      'Answers on percentage cutoffs, plus/minus scales, GPA points per letter and why your school’s scale may differ from the common one.',
    ctaText: 'Enter a percentage or pick a letter above to see its range, GPA points and full scale table.',
  },
  'average-grade-calculator': {
    eyebrow: 'Average Grade FAQs',
    title: 'Average Grade Calculator FAQs',
    description:
      'Answers on the overall average grade formula, averaging scores out of different totals, weights and credits, the average needed on remaining tests, mean versus median, C grades and GPA.',
    ctaText: 'Paste your scores above to see your average, median and letter grade update instantly.',
  },
  'grading-scale': {
    eyebrow: 'Grading Scale FAQs',
    title: 'Grading Scale Chart FAQs',
    description:
      'Answers on how each question is worth a share of 100%, how many answers you can miss for an A, and how rounding works.',
    ctaText: 'Pick your test size above to open the score chart for every number of wrong answers.',
  },
};

export const TOOL_FAQS: Record<ToolKey, ToolFaqConfig> = {
  quick: {
    eyebrow: 'Grading & Weighting FAQs',
    title: 'Grade Calculator & Scoring FAQs',
    description:
      'Clear, mathematically verified answers to common questions about weighted grade averages, grading scales, and final exam target scores.',
    ctaText: 'Test a hypothetical score with our Target Grade Simulator above!',
    items: [
      {
        id: 'how-are-grades-calculated',
        category: 'general',
        question: 'How are grades calculated?',
        answer:
          'Grades are calculated by dividing the total points or weighted percentage earned by the total points or weight possible, then multiplying by 100 to produce a percentage. For points-based systems: Grade % = (Total Points Earned ÷ Total Points Possible) × 100. For weighted systems: Grade % = Σ (Category Score % × Category Weight) ÷ Total Active Weight. The resulting percentage is mapped to an institutional letter grade (e.g., 90–100% for an A, 80–89% for a B).',
      },
      {
        id: 'what-is-weighted-grade',
        category: 'weighted',
        question: 'What is a weighted grade?',
        answer:
          'A weighted grade is a course grading calculation where different assignment categories (such as Homework, Quizzes, Midterms, and Finals) carry varying proportional percentage weights toward your overall course grade, rather than every raw point having equal value. For example, if Quizzes count for 20%, Midterms for 30%, and the Final Exam for 50%, an exam carries 2.5 times the impact of homework on your final GPA.',
      },
      {
        id: 'how-to-calculate-weighted',
        category: 'weighted',
        question: 'How do I calculate my weighted grade average?',
        answer:
          'To calculate a weighted grade average: 1) Convert each assessment score into a percentage (points earned ÷ points possible × 100); 2) Multiply each percentage by its category weight (in decimal or percentage form); 3) Sum all weighted contributions together; and 4) Divide by the total sum of the weights. If your category weights already total 100%, the sum of the weighted scores directly equals your final course grade percentage.',
      },
      {
        id: 'final-exam-target-grade',
        category: 'exams',
        question: 'What grade do I need on my final exam to reach my target grade?',
        answer:
          'You can calculate this with the formula: Required Score = [Target Grade - (Current Grade × Current Weight)] ÷ Final Exam Weight. For example, if you hold an 86% average representing 80% of your course and want an A (90%) with a 20% final exam, you need [90 - (86 × 0.80)] ÷ 0.20 = [90 - 68.8] ÷ 0.20 = 106% (which would require extra credit). Our built-in Target Grade & Simulator above solves this dynamically for you!',
      },
      {
        id: 'points-vs-weighted',
        category: 'general',
        question: 'What is the difference between points-based and weighted grading?',
        answer:
          'In a points-based grading system, every earned point contributes equally to your overall total: your grade is simply total points earned divided by total points possible. In a weighted grading system, assignment categories have fixed percentages (e.g., Homework 20%, Exams 50%), meaning a 10-point quiz in an exam category carries significantly more impact than a 10-point homework assignment.',
      },
      {
        id: 'weights-not-100',
        category: 'weighted',
        question: 'Can I calculate my grade if weights do not add up to 100%?',
        answer:
          'Yes! Our Easy Grade Tool automatically normalizes relative weights. The algorithm divides your accumulated weighted score by the sum of currently active weights entered. This ensures mathematically precise tracking at any point during the semester, even when future assignments or final exam weights have not yet occurred.',
      },
      {
        id: 'rounding-percentages',
        category: 'general',
        question: 'Does this grade calculator round the final percentage?',
        answer:
          'The displayed percentage is rounded to one decimal place (e.g., 89.6%) for clean, clear readability. However, institutional letter grade classifications and trajectory projections are evaluated using unrounded floating-point precision, ensuring your letter grade assignment strictly follows the selected standard or plus/minus scale.',
      },
      {
        id: 'points-to-pass',
        category: 'exams',
        question: 'How many points do I need to pass my class?',
        answer:
          'First check your course syllabus for the minimum passing threshold (typically 60% for a D or 70% for a C). Enter all completed scores into the calculator, then set your target grade to that passing percentage in the Target Grade Simulator to see the exact minimum score and points required on your remaining assignments.',
      },
    ],
  },
  gpa: {
    eyebrow: 'Academic Standing FAQs',
    title: 'How is GPA Calculated? College & University FAQs',
    description:
      'Authoritative answers regarding 4.0 grade point averages, credit weighting, quality points, and registrar transcript policies.',
    ctaText: 'Add your semester courses above to see your cumulative GPA and quality points update in real time.',
    items: [
      {
        id: 'gpa-how-4-scale',
        category: 'gpa',
        question: 'How to calculate your GPA on a 4.0 scale?',
        answer:
          'Turn each letter into grade points (A = 4.0, B = 3.0, C = 2.0, D = 1.0, F = 0.0), multiply by the course credits, add the results and divide by the total credits. For example, an A in a 3-credit course, a B+ in a 4-credit course and a B in a 3-credit course give 12 + 13.2 + 9 = 34.2 quality points over 10 credits, a GPA of 3.42.',
      },
      {
        id: 'gpa-from-percentages',
        category: 'gpa',
        question: 'How to calculate my GPA if only percentages are known?',
        answer:
          'Convert each percentage to a letter first, using the school’s scale, then follow the usual steps. The letter grade calculator and the letter grade to GPA converter handle that first step, and the results can be typed straight into this page.',
      },
      {
        id: 'gpa-calculate-by-hand',
        category: 'gpa',
        question: 'How do I calculate GPA from semester grades without a tool?',
        answer:
          'List each course with its credits and grade points, multiply the two, add the products and divide by the credits. Anyone who needs to calculate a GPA this way for a transcript can check the answer against the calculator above.',
      },
      {
        id: 'gpa-how-i-calculate',
        category: 'gpa',
        question: 'How I calculate my GPA when a transcript shows plus and minus grades?',
        answer:
          'Use the plus/minus values the school publishes, commonly A− = 3.7, B+ = 3.3, B− = 2.7 and so on. Switching the calculator to the plus/minus scale applies those values automatically, and the GPA compute step is still one credit-weighted average.',
      },
      {
        id: 'gpa-with-grades',
        category: 'gpa',
        question: 'Is there a GPA calculator with grades instead of percentages?',
        answer:
          'Yes. Each course row accepts a letter grade, so a student can type A, B+ or C− directly. A percentage can be entered instead and the page converts it using the chosen scale.',
      },
      {
        id: 'gpa-average-calculator',
        category: 'gpa',
        question: 'Is this an average GPA calculator?',
        answer:
          'In practice, yes. It averages grade points, weighted by credit hours, across every course entered. That is different from a simple mean, because a 4-credit course counts more than a 1-credit course.',
      },
      {
        id: 'gpa-meter',
        category: 'gpa',
        question: 'What is a GPA meter?',
        answer:
          'The phrase is an informal way to ask where a GPA stands right now. The result card on this page shows the GPA, the quality points behind it and the total credits, which is the quickest read-out of a student’s standing.',
      },
      {
        id: 'gpa-score-calculator',
        category: 'gpa',
        question: 'How does a score GPA calculator treat test percentages?',
        answer:
          'A score GPA calculator first converts the percentage into a letter on the chosen scale and then into grade points. Because the cutoffs differ, an 86% can be a B on the standard scale (3.0 points) and a B on the plus/minus scale too, but 87% becomes a B+ (3.3 points) only on the plus/minus scale.',
      },
      {
        id: 'gpa-calculation',
        category: 'gpa',
        question: 'How do I calculate my GPA?',
        answer:
          'Your Grade Point Average (GPA) is calculated on a 4.0 scale by converting each course letter grade into numerical grade points (A = 4.0, A- = 3.7, B+ = 3.3, B = 3.0, B- = 2.7, C+ = 2.3, C = 2.0, D = 1.0, F = 0.0). You multiply each grade point value by the course credit hours to obtain "quality points", add all quality points together across all courses, and divide by the total number of credits attempted.',
      },
      {
        id: 'projected-gpa',
        category: 'gpa',
        question: 'How do I calculate a projected GPA?',
        answer:
          'A projected GPA estimates where your GPA will land after the courses you are taking now. Enter your current cumulative GPA and credits earned, add this term\'s courses with the grades you expect, and divide total quality points by total credits. For example, 60 credits at a 3.2 GPA is 192 quality points; adding 15 credits at 3.6 gives 54 more, so (192 + 54) ÷ 75 = 3.28.',
      },
      {
        id: 'gpa-for-73-percent',
        category: 'gpa',
        question: 'What GPA is a 73?',
        answer:
          'A 73% is a C on most grading scales, which is worth 2.0 on the 4.0 scale. On the plus/minus scale 73 is the lowest C (2.0): 70 to 72 is a C- (1.7) and 77 starts a C+ (2.3). Some schools set their own cutoffs, so check your syllabus.',
      },
      {
        id: 'gpa-quality-points-chart',
        category: 'gpa',
        question: 'What is the quality points chart for a 3-credit course?',
        answer:
          'Quality points are grade points multiplied by credits. For a 3-credit course on the 4.0 scale: A (4.0) = 12, A- (3.7) = 11.1, B+ (3.3) = 9.9, B (3.0) = 9, B- (2.7) = 8.1, C+ (2.3) = 6.9, C (2.0) = 6, D (1.0) = 3 and F (0.0) = 0.',
      },
      {
        id: 'weighted-vs-unweighted-gpa',
        category: 'gpa',
        question: 'What is the difference between weighted and unweighted GPA?',
        answer:
          'An unweighted GPA is calculated on a standard 4.0 scale where course difficulty is not considered (an A in a standard class and an A in an advanced course both equate to 4.0). A weighted GPA assigns additional weight (often up to 5.0) to advanced, AP, IB, or Honors courses to reflect increased academic challenge and effort.',
      },
      {
        id: 'quality-points-explained',
        category: 'gpa',
        question: 'What are quality points and how are they calculated?',
        answer:
          'Quality points represent the numerical value of a grade relative to course credit hours. They are determined by multiplying the assigned grade points for the letter grade by the number of credit hours for that course. For instance, an "A" (4.0 points) in a 4-credit course yields 16 quality points (4.0 × 4 = 16), which are then summed across all courses to find your GPA.',
      },
      {
        id: 'semester-vs-cumulative-gpa',
        category: 'gpa',
        question: 'What is the difference between semester GPA and cumulative GPA?',
        answer:
          'A semester GPA reflects your academic performance during a single semester or term. The cumulative GPA represents the overall average of all quality points earned across every semester throughout your entire college or university enrollment.',
      },
      {
        id: 'retaken-courses-gpa',
        category: 'gpa',
        question: 'How do retaken courses affect my GPA?',
        answer:
          'Most institutions have grade forgiveness or repeat policies where the new grade replaces the prior grade in the cumulative GPA calculation, significantly improving your academic standing. However, both course attempts usually remain recorded on your official university transcript.',
      },
      {
        id: 'good-gpa-guidelines',
        category: 'gpa',
        question: 'What is considered a good GPA for college honors and graduate school?',
        answer:
          'Most colleges require a 2.0 GPA for good academic standing. A GPA of 3.0 or above is generally competitive for graduate school programs, while a GPA of 3.5 or higher typically qualifies students for Dean\'s List recognition, honors societies, and academic distinction (cum laude).',
      },
      {
        id: 'gpa-weighted-vs-unweighted',
        category: 'gpa',
        question: 'What is the difference between weighted and unweighted GPA?',
        answer:
          'An unweighted GPA uses the plain 4.0 scale, so an A is 4.0 in every course. A weighted GPA adds bonus points for harder courses, commonly +0.5 for honors and +1.0 for AP or IB, so the scale can reach 5.0. Weighted vs unweighted GPA is mostly a high school distinction, and your transcript may list both.',
      },
      {
        id: 'gpa-unweighted-meaning',
        category: 'gpa',
        question: 'What is the unweighted GPA meaning?',
        answer:
          'Unweighted GPA means a grade point average that ignores course difficulty. Every A counts 4.0, every B 3.0 and so on, whether the class is regular, honors or AP. The calculator above gives an unweighted GPA.',
      },
      {
        id: 'gpa-weighted-calculator',
        category: 'gpa',
        question: 'Is there a weighted GPA calculator here?',
        answer:
          'This calculator works on the 4.0 scale (with an optional 4.33 for A+). To find a weighted GPA, add your school’s bonus, such as +0.5 for honors or +1.0 for AP, to the grade points of those courses, then multiply by credits and divide by total credits. Bonus values vary, so use your school’s weighting table.',
      },
      {
        id: 'gpa-college-admissions',
        category: 'gpa',
        question: 'How does weighted GPA affect college admissions?',
        answer:
          'Many colleges recalculate GPA on their own scale, often unweighted, and look at the rigor of the courses you chose. A weighted GPA shows that you took harder classes, but it is not directly comparable between schools because weighting systems differ. Your school profile explains how your school weights courses.',
      },
      {
        id: 'gpa-high-school-how',
        category: 'gpa',
        question: 'How do I calculate my high school GPA?',
        answer:
          'Convert each final course grade to grade points (A = 4.0, B = 3.0, C = 2.0, D = 1.0, F = 0.0), multiply by the credit value, add the results and divide by total credits. If every course has the same credit, divide by the number of courses. A student with 4.0, 3.0, 4.0 and 3.0 in four equal courses has a 3.5 GPA.',
      },
      {
        id: 'gpa-high-school-calculator',
        category: 'gpa',
        question: 'Can I use this as a high school GPA calculator?',
        answer:
          'Yes. Enter each course with its letter grade and credit value (use 1.0 for equal credits) to get your semester GPA, then add your prior GPA and credits for a cumulative figure. It gives the unweighted GPA on a 4.0 scale.',
      },
      {
        id: 'gpa-scale-4-high-school',
        category: 'gpa',
        question: 'What is the GPA scale 4.0 high school students use?',
        answer:
          'On the common 4.0 scale an A is 4.0, a B is 3.0, a C is 2.0, a D is 1.0 and an F is 0.0, with plus and minus grades such as A- at 3.7 and B+ at 3.3. Schools that weight courses add bonus points for honors, AP or IB classes on top of this.',
      },
      {
        id: 'gpa-needed-for-college',
        category: 'gpa',
        question: 'What GPA do I need to get into college?',
        answer:
          'It depends on the college. Community colleges and open-admission schools often set no minimum, many state universities report a middle range of roughly 3.0 to 3.8, and the most selective colleges typically admit students close to 4.0 unweighted. Check each school’s admitted-student profile.',
      },
      {
        id: 'gpa-semester-vs-cumulative',
        category: 'gpa',
        question: 'What is the difference between a semester GPA and a cumulative GPA?',
        answer:
          'A semester GPA covers the courses from one term only. A cumulative GPA combines every term by adding all quality points and dividing by all credits attempted. The calculator’s prior GPA and credits fields turn a semester result into a cumulative GPA.',
      },
      {
        id: 'gpa-quality-points',
        category: 'gpa',
        question: 'How do I use a quality points calculator?',
        answer:
          'Quality points are grade points multiplied by credit hours. An A (4.0) in a 3-credit course is 12.0 quality points. Add the quality points for all courses and divide by the total credits to get your GPA. The calculator shows the quality points for each course.',
      },
    ],
  },
  cgpa: {
    eyebrow: 'CGPA Conversion & University FAQs',
    title: 'CGPA to Percentage Conversion FAQs',
    description:
      'Verified answers on converting CGPA to percentage across 10.0, 5.0, and 4.0 grading scales, official university circulars, and SGPA to CGPA formulas.',
    ctaText: 'Select your grading scale and university above to compute your exact percentage and download an official PDF report.',
    items: [
      {
        id: 'cgpa-75',
        category: 'cgpa',
        question: 'What is 7.5 CGPA in percentage?',
        answer:
          'Using the common CGPA × 9.5 conversion, 7.5 CGPA in percentage is 7.5 × 9.5 = 71.25%. The university’s own regulation overrides the standard multiplier, so pick the institution in the calculator when it is listed.',
      },
      {
        id: 'cgpa-52',
        category: 'cgpa',
        question: 'What is 5.2 CGPA in percentage, and how does CGPA 5.2 to percentage differ from 5.4?',
        answer:
          'Applying the × 9.5 rule, 5.2 CGPA in percentage is 49.4%. For comparison, CGPA 5.4 to percentage is 51.3%, so each 0.2 of CGPA is worth 1.9 percentage points under this formula.',
      },
      {
        id: 'cgpa-575',
        category: 'cgpa',
        question: 'How to convert 5.75 CGPA to percentage?',
        answer:
          'Multiply by 9.5: 5.75 × 9.5 = 54.625, which rounds to 54.63%. Most boards and universities round to one or two decimals, so the printed figure can differ slightly between institutions.',
      },
      {
        id: 'cgpa-reverse',
        category: 'cgpa',
        question: 'How can 64.8 percentage in CGPA be worked out?',
        answer:
          'Reverse the multiplier: 64.8 ÷ 9.5 = 6.82 CGPA on the 10-point scale. The reverse direction has the same caveat as the forward one: an institution with its own formula uses that formula.',
      },
      {
        id: 'cgpa-10-point',
        category: 'cgpa',
        question: 'How to convert CGPA to percentage on a 10 point scale?',
        answer:
          'Convert CGPA to percentage on a 10 point scale with the formula the institution publishes. The most widespread one is percentage = CGPA × 9.5, and a few universities use CGPA × 10 instead, so a result should be compared with the official ordinance.',
      },
      {
        id: 'cgpa-out-of-4',
        category: 'cgpa',
        question: 'How to convert CGPA into percentage out of 4?',
        answer:
          'A 4-point CGPA is not converted with the 9.5 multiplier, which belongs to the 10-point system. Many 4-point institutions publish no percentage rule at all; a plain ratio such as CGPA ÷ 4 × 100 is only a rough comparison, not an official conversion, so ask the registrar if a percentage is required.',
      },
      {
        id: 'cgpa-srm',
        category: 'cgpa',
        question: 'Is there an SRM CGPA calculator?',
        answer:
          'Yes. SRM Institute of Science and Technology states that percentage equals CGPA × 10, so an 8.2 CGPA is 82%. Choose SRM in the university list and the calculator applies that rule.',
      },
      {
        id: 'cgpa-qpa',
        category: 'cgpa',
        question: 'Is there a QPA to GPA calculator?',
        answer:
          'QPA stands for quality point average, the name some universities use for what others call GPA. Where the QPA uses the same 4.0 grade points, the number is the same and the GPA calculator can be used directly; a school that defines QPA differently publishes its own rule.',
      },
      {
        id: 'how-to-convert-cgpa-to-percentage',
        category: 'cgpa',
        question: 'How do I convert my 10-point CGPA to a percentage?',
        answer:
          'Under the standard UGC and CBSE 10-point grading system, multiply your Cumulative Grade Point Average (CGPA) by 9.5: Percentage = CGPA × 9.5. For example, an 8.2 CGPA equals 8.2 × 9.5 = 77.90%. However, if your university prescribes a specific ordinance (such as VTU, MAKAUT, GTU, Mumbai University, or Anna University), you should apply your institution’s official formula.',
      },
      {
        id: 'vtu-makaut-sppu-formula',
        category: 'cgpa',
        question: 'Why do VTU, MAKAUT, AKTU, and SPPU subtract 0.75 from CGPA before multiplying by 10?',
        answer:
          'Technical universities under AICTE/CBCS frameworks—such as VTU, MAKAUT (WBUT), AKTU, BAMU, and SPPU (2019 CBCS rulebook)—use grade point bands where the midpoint of each letter grade band sits 7.5 percentage points below the band maximum. Subtracting 0.75 from the CGPA and multiplying by 10 aligns the grade point average with the true mark band midpoint: Percentage = (CGPA − 0.75) × 10.',
      },
      {
        id: 'mumbai-university-cgpa-formula',
        category: 'cgpa',
        question: 'What is the official Mumbai University CGPA to percentage formula?',
        answer:
          'For general undergraduate and postgraduate programmes under CBSGS/CBCS, the University of Mumbai prescribes Percentage = (7.1 × CGPA) + 11. For Faculty of Technology (Engineering) programmes from 2015–16 onward, the circular specifies Percentage = (7.4 × CGPA) + 12 for CGPA ≥ 7.0 and Percentage = (7.1 × CGPA) + 12 for CGPA < 7.0.',
      },
      {
        id: 'convert-4-and-5-scale-cgpa',
        category: 'cgpa',
        question: 'How do I convert a 4.0 or 5.0 scale CGPA into a percentage?',
        answer:
          'On a 4.0 grading scale (used in the US, Canada, and Pakistani universities under HEC such as NUST, LUMS, COMSATS, and FAST), the linear conversion formula is Percentage = CGPA × 25 (e.g., 3.40 CGPA = 85.00%). On a 5.0 grading scale, the formula is Percentage = CGPA × 20 (e.g., 4.10 CGPA = 82.00%).',
      },
      {
        id: 'difference-between-sgpa-and-cgpa',
        category: 'cgpa',
        question: 'What is the difference between SGPA and CGPA, and how do I convert SGPA to CGPA?',
        answer:
          'SGPA (Semester Grade Point Average) measures your academic performance in a single semester, whereas CGPA (Cumulative Grade Point Average) is the credit-weighted average of your SGPAs across all completed semesters: CGPA = Σ(Semester SGPA × Semester Credits) ÷ Total Credits. If all semesters carry equal credits, CGPA is the simple arithmetic mean of all semester SGPAs.',
      },
      {
        id: 'which-formula-for-job-applications',
        category: 'cgpa',
        question: 'Which conversion formula should I enter on government job, GATE, CAT, or PSU forms?',
        answer:
          'Always check the reverse side of your university marksheet or official transcript first. Recruitment portals (such as UPSC, GATE, IBPS, and CAT) require you to use the exact conversion formula printed by your university or degree board. Only if your university states that no official conversion formula exists should you apply the standard UGC/AICTE multiplier (typically × 9.5 or × 10 as specified in the exam notification).',
      },
    ],
  },
  tip: {
    eyebrow: 'Gratuity & Etiquette FAQs',
    title: 'Tipping & Bill Splitting FAQs',
    description:
      'Answers to common tipping dilemmas, pre-tax versus post-tax gratuities, mental calculation tricks, and group bill splits.',
    ctaText: 'Use the tip percentage selector and split fields above for instant, fair per-person amounts.',
    items: [
      {
        id: 'tip-gratuity-calculator',
        category: 'tip',
        question: 'What is a gratuity calculator?',
        answer:
          'It is another name for a tip calculator: it multiplies the bill by the tip percentage and, when needed, splits the total between several people. Gratuity is simply the formal word for a tip.',
      },
      {
        id: 'tip-gratuity-math',
        category: 'tip',
        question: 'What is gratuity in math?',
        answer:
          'Gratuity math is percentage math. The gratuity equals the bill times the rate, so 18% of $60.00 is 0.18 × 60 = $10.80, and the total to pay is $70.80.',
      },
      {
        id: 'tip-tax-gratuity',
        category: 'tip',
        question: 'Is there a tax gratuity calculator that tips on the pre-tax amount?',
        answer:
          'Many diners tip on the pre-tax subtotal, which is a matter of habit rather than a rule. To do that here, enter the subtotal before tax as the bill, then add the sales tax yourself afterwards.',
      },
      {
        id: 'tip-america',
        category: 'tip',
        question: 'What is a normal rate on a tip calculator America would use?',
        answer:
          'For table service in the United States, 15% to 20% of the pre-tax bill is the common range, with 18% a frequent default. Counter service, delivery and bars follow different habits, so the calculator lets the percentage be changed.',
      },
      {
        id: 'standard-tip-percentage',
        category: 'tip',
        question: 'What is the standard tip percentage in restaurants?',
        answer:
          'In North America, standard gratuity for sit-down restaurant dining is 15% to 20% of the bill subtotal. Exceptional service is commonly rewarded with 20% to 25%, while 10% to 15% is reserved for below-average service. For delivery and rideshares, 15% to 20% is also customary.',
      },
      {
        id: 'tip-before-or-after-tax',
        category: 'tip',
        question: 'Should I calculate the tip before or after sales tax?',
        answer:
          'Etiquette experts recommend calculating your tip on the pre-tax bill amount. Sales tax is a government levy rather than compensation for hospitality service, though calculating on the total amount is common when using card terminal presets.',
      },
      {
        id: 'mental-math-20-tip',
        category: 'tip',
        question: 'What is the quickest mental math trick to calculate a 20% tip?',
        answer:
          'Move the decimal point of your bill one space to the left to find 10%, then double that number. For example, on a $54.00 check, 10% is $5.40, and doubling it yields $10.80 for a 20% tip.',
      },
      {
        id: 'how-to-split-bill-fairly',
        category: 'tip',
        question: 'How do I split a restaurant bill evenly among friends?',
        answer:
          'Add the bill subtotal, taxes, and tip together to get the grand total, then divide equally by the number of people dining. Our tip calculator does this instantly and shows both individual shares and total gratuity.',
      },
      {
        id: 'international-tipping-customs',
        category: 'tip',
        question: 'What are international tipping customs in places like Japan or Europe?',
        answer:
          'Tipping norms vary globally. In Japan, tipping is not expected and can even be considered rude or confusing because exceptional service is built into standard wages. In many European nations, service charges are included by law ("service compris"), and patrons simply leave small change or round up by 5% to 10%.',
      },
      {
        id: 'tipping-takeout-counter',
        category: 'tip',
        question: 'Should I tip on takeout or counter-service orders?',
        answer:
          'Tipping on takeout is optional. Leaving 10% or rounding up with change is considered courteous for packing and preparation, especially on large, complex, or customized food orders.',
      },
      {
        id: 'tip-split-bill-calculator',
        category: 'tip',
        question: 'How does the split bill calculator work?',
        answer:
          'Enter the bill, choose a tip percentage and set the number of people. The tip is added to the bill and the total is divided evenly, so you see the tip, the total and the amount each person pays. A $120 bill with an 18% tip split four ways is $35.40 per person.',
      },
    ],
  },
  percentage: {
    eyebrow: 'Math & Formulas FAQs',
    title: 'Percentage Calculation FAQs',
    description:
      'Step-by-step answers on how to calculate a percentage, percentage increase and decrease, percentage difference, reverse percentage, marks and grades.',
    ctaText: 'Use the percentage calculators above to compute increases, decreases, and fractions instantly.',
    items: [
      {
        id: 'pct-grading',
        category: 'percentage',
        question: 'Can it work as a grading percentage calculator?',
        answer:
          'Yes. Enter the points earned and the points possible and the percentage appears. For a graded test with a letter, the test grade calculator adds the scale.',
      },
      {
        id: 'percentage-change-formula',
        category: 'math',
        question: 'How do I calculate percentage change (increase or decrease)?',
        answer:
          'To calculate percentage change: subtract the original value from the new value, divide by the absolute value of the original number, and multiply by 100: [ (New - Old) ÷ |Old| ] × 100. A positive result indicates a percentage increase, while a negative result represents a percentage decrease.',
      },
      {
        id: 'what-percent-x-of-y',
        category: 'math',
        question: 'What is the formula to find what percent X is of Y?',
        answer:
          'Divide X by Y, then multiply the result by 100: (X ÷ Y) × 100. For example, to find what percentage 25 is of 80: (25 ÷ 80) × 100 = 31.25%.',
      },
      {
        id: 'calculate-discount-savings',
        category: 'math',
        question: 'How do you calculate a sale discount and savings amount?',
        answer:
          'Multiply the original price by the discount percentage divided by 100 to get your savings. Then subtract that savings amount from the original price to find the final sale price. For example, 30% off $80 is: $80 × 0.30 = $24 savings, leaving a sale price of $56.',
      },
      {
        id: 'percentages-greater-than-100',
        category: 'math',
        question: 'Can percentages be greater than 100%?',
        answer:
          'Yes! A percentage greater than 100% simply means the quantity is larger than the original base reference. For instance, if an investment grows from $100 to $250, that represents a 150% increase and 250% of the original value.',
      },
      {
        id: 'percentage-vs-percentile',
        category: 'math',
        question: 'What is the difference between a percentage and a percentile?',
        answer:
          'A percentage expresses a fraction or proportion out of 100 (e.g., scoring 85% on an exam means 85 out of 100 questions correct). A percentile describes relative rank compared to others (e.g., scoring in the 90th percentile means you scored higher than 90% of test takers).',
      },
      {
        id: 'percentage-percent-change-calculator',
        category: 'percentage',
        question: 'How do I use the percent change calculator?',
        answer:
          'Enter the starting value and the new value. The percent change is (new − old) ÷ old × 100: going from 80 to 100 is a 25% increase, and going from 100 to 80 is a 20% decrease. A positive result means growth and a negative result means a drop.',
      },
      {
        id: 'how-to-use-a-percentage-calculator',
        category: 'percentage',
        question: 'How do I use a percentage calculator?',
        answer:
          'Pick the question that matches your problem and fill in the two blanks. Use the first box for X% of a number, the second for X is what percent of Y, and the third for the percentage change from one value to another. The result updates as you type. For example, 20% of 150 is 30, 30 is 20% of 150, and going from 100 to 125 is a 25% increase.',
      },
      {
        id: 'what-is-a-percentage-calculator',
        category: 'percentage',
        question: 'What is a percentage calculator?',
        answer:
          'A percentage calculator is an online tool that does percent math for you: finding a percentage of a number, working out what percent one number is of another, and measuring how much a value rose or fell in percent. It applies the standard formulas, such as (part ÷ whole) × 100, so you do not have to do the division and multiplication by hand.',
      },
      {
        id: 'how-to-calculate-percentage-increase',
        category: 'percentage',
        question: 'How do I calculate a percentage increase?',
        answer:
          'Subtract the old value from the new value, divide by the old value, and multiply by 100. A price that rises from 40 to 50 is (50 − 40) ÷ 40 × 100 = 25% higher. In the percentage change box, enter 40 as the start and 50 as the end to get +25%.',
      },
      {
        id: 'how-to-calculate-percentage-decrease',
        category: 'percentage',
        question: 'How do I calculate a percentage decrease, reduction or drop?',
        answer:
          'Use the same formula as an increase: (new − old) ÷ old × 100. If the answer is negative, it is a decrease. A value that falls from 200 to 150 is (150 − 200) ÷ 200 × 100 = −25%, so it dropped by 25%. Enter 200 as the start and 150 as the end in the percentage change box to see −25%.',
      },
      {
        id: 'increase-or-decrease-number-by-percentage',
        category: 'percentage',
        question: 'How do I increase or decrease a number by a percentage?',
        answer:
          'To increase a number by a percentage, multiply it by (1 + percent ÷ 100). To decrease it, multiply by (1 − percent ÷ 100). So 120 increased by 15% is 120 × 1.15 = 138, and 120 reduced by 15% is 120 × 0.85 = 102. You can also find 15% of 120 in the first box (18) and add or subtract it.',
      },
      {
        id: 'percentage-difference-between-two-numbers',
        category: 'percentage',
        question: 'How do I find the percentage difference between two numbers?',
        answer:
          'Percentage difference compares two values without treating either as the starting point. Divide the absolute difference by the average of the two numbers, then multiply by 100. For 50 and 75 the difference is 25 and the average is 62.5, so the percentage difference is 40%. Percentage change from 50 to 75 is a different figure, +50%, because it uses 50 as the base.',
      },
      {
        id: 'percentage-change-vs-percentage-difference',
        category: 'percentage',
        question: 'What is the difference between percentage change and percentage difference?',
        answer:
          'Percentage change has a clear starting value and divides by it, so the direction matters: 50 to 75 is +50%, while 75 to 50 is −33.33%. Percentage difference divides by the average of both numbers, so the order does not matter and the result is the same either way. Use change for before and after, and difference when comparing two values side by side.',
      },
      {
        id: 'reverse-percentage-original-value',
        category: 'percentage',
        question: 'How do I find the original number after a percentage increase or discount (reverse percentage)?',
        answer:
          'Divide the final value by the multiplier. After a 20% discount the multiplier is 0.80, so a sale price of 60 came from 60 ÷ 0.80 = 75. After a 20% increase the multiplier is 1.20, so a final value of 60 came from 60 ÷ 1.20 = 50. Do not simply add or subtract 20% from the final price, because the percentage applied to the original number, not the new one.',
      },
      {
        id: 'how-to-calculate-percentage-of-marks-or-grades',
        category: 'percentage',
        question: 'How do I calculate a percentage for marks, test scores or grades?',
        answer:
          'Divide the marks you scored by the total marks and multiply by 100. A score of 45 out of 50 is (45 ÷ 50) × 100 = 90%, and 425 out of 500 is 85%. In the second box, enter your score as X and the total as Y. For a weighted course grade or a letter grade, use the grade calculators on this site.',
      },
      {
        id: 'how-to-calculate-average-percentage',
        category: 'percentage',
        question: 'How do I calculate an average percentage?',
        answer:
          'If every item has the same total, add the percentages and divide by how many there are. If the totals differ, add all the points you earned and divide by all the points possible. For example, 80% on a 50-point quiz and 90% on a 100-point exam is (40 + 90) ÷ (50 + 100) = 86.67%, not the simple average of 85%.',
      },
      {
        id: 'percentage-without-calculator',
        category: 'percentage',
        question: 'How can I calculate a percentage without a calculator?',
        answer:
          'Start from 10% and 1%. To find 10%, move the decimal one place left, and for 1%, move it two places. Then combine them: 15% of 80 is 10% (8) plus half of that (4), which is 12. For 25%, take a quarter, and for 50%, halve the number. Multiply or add these pieces to build any percentage.',
      },
      {
        id: 'convert-percentage-decimal-fraction',
        category: 'percentage',
        question: 'How do I convert a percentage to a decimal or fraction, and back?',
        answer:
          'Divide the percentage by 100 to get a decimal: 15% becomes 0.15, and 75% becomes 0.75. To go from a decimal to a percentage, multiply by 100, so 0.375 is 37.5%. For a fraction, divide the numerator by the denominator and multiply by 100: 3/8 is 0.375, which is 37.5%.',
      },
      {
        id: 'percentage-calculator-weight-loss',
        category: 'percentage',
        question: 'How do I calculate weight loss as a percentage?',
        answer:
          'Subtract your current weight from your starting weight, divide by the starting weight, and multiply by 100. Going from 200 lb to 180 lb is (200 − 180) ÷ 200 × 100 = 10% weight loss. In the percentage change box, enter 200 and 180 and read the result as −10%, which means a 10% loss.',
      },
      {
        id: 'percentage-formulas-excel-google-sheets',
        category: 'percentage',
        question: 'What are the percentage formulas for Excel and Google Sheets?',
        answer:
          'To get what percent A1 is of B1, use =A1/B1 and format the cell as Percentage. For percentage change from A1 to B1, use =(B1-A1)/A1. To increase A1 by 10%, use =A1*(1+10%), and to find 10% of A1, use =A1*10%. Both Excel and Google Sheets use the same formulas.',
      },
      {
        id: 'percentage-points-vs-percent',
        category: 'percentage',
        question: 'What is the difference between percentage points and percent?',
        answer:
          'Percentage points are the plain subtraction of two percentages, while percent change compares them proportionally. If a rate rises from 10% to 12%, that is an increase of 2 percentage points, but it is a 20% increase in the rate itself, because 2 ÷ 10 = 0.20.',
      },
      {
        id: 'markup-vs-margin-percentage',
        category: 'percentage',
        question: 'How do I calculate profit, markup and margin percentages?',
        answer:
          'Markup is profit divided by cost, and margin is profit divided by selling price, both times 100. If an item costs 80 and sells for 100, the profit is 20, so the markup is 20 ÷ 80 = 25% and the margin is 20 ÷ 100 = 20%. Use the second box with profit as X and cost or price as Y.',
      },
    ],
  },
  loan: {
    eyebrow: 'Loan Calculator FAQs',
    title: 'Loan Calculator FAQs: Payments, Interest & Payoff',
    description:
      'Answers on how a loan calculator works, car and personal loan payments, total interest, amortization, APR, extra payments and paying a loan off early.',
    ctaText: 'Adjust your loan amount, APR or term above to see your monthly payment and interest breakdown.',
    items: [
      {
        id: 'loan-provisional-quote',
        category: 'loan',
        question: 'Is the result a provisional loan quote?',
        answer:
          'No. The result is an estimate of the monthly payment and total interest from the amount, rate and term entered. A provisional loan quote from a lender also reflects fees and a rate set after a credit check, so the two figures can differ.',
      },
      {
        id: 'monthly-loan-formula',
        category: 'loan',
        question: 'How is a monthly loan payment calculated?',
        answer:
          'Monthly payments use the standard amortization formula: M = P [i(1 + i)^n] ÷ [(1 + i)^n − 1], where M is the monthly payment, P is the loan amount, i is the monthly interest rate (annual APR ÷ 12) and n is the total number of monthly payments.',
      },
      {
        id: 'how-loan-calculator-works',
        category: 'loan',
        question: 'How does a loan calculator work?',
        answer:
          'Enter the amount you want to borrow, the annual interest rate (APR) and the term in years. The calculator applies the fixed-rate amortization formula to find your monthly payment, adds up every payment to show total interest and total cost, and builds a year-by-year schedule of principal paid, interest paid and remaining balance.',
      },
      {
        id: 'car-loan-payment',
        category: 'loan',
        question: 'How do I calculate a car loan payment?',
        answer:
          'Subtract your down payment and any trade-in value from the vehicle price, then enter that balance as the loan amount along with the lender\'s APR and the term. For example, a $30,000 car loan at 7% APR costs about $511.47 a month over 6 years or about $452.78 a month over 7 years, with roughly $1,200 more total interest on the longer term.',
      },
      {
        id: 'total-interest-on-loan',
        category: 'loan',
        question: 'How much interest will I pay on a loan?',
        answer:
          'Total interest is the sum of all your monthly payments minus the amount you borrowed. A $25,000 loan at 7.5% APR over 5 years has a payment of about $500.95 and total interest of $5,056.92. A lower APR, a shorter term or a larger down payment all reduce it.',
      },
      {
        id: 'principal-vs-interest',
        category: 'loan',
        question: 'What is the difference between principal and interest?',
        answer:
          'Principal is the amount you borrowed and owe back to the lender. Interest is the fee the lender charges for lending it. Each monthly payment is split between interest on the remaining balance and a reduction of the principal.',
      },
      {
        id: 'loan-amortization-schedule',
        category: 'loan',
        question: 'What is loan amortization?',
        answer:
          'Amortization is paying off a debt through equal, scheduled installments. In the early months most of each payment goes toward interest. As the balance falls, a growing share of every payment reduces principal, which the amortization schedule shows year by year.',
      },
      {
        id: 'extra-payments-impact',
        category: 'loan',
        question: 'How do extra payments help pay off a loan early?',
        answer:
          'Any amount paid above the scheduled payment goes to principal, which lowers the interest charged in every later month. On a $25,000 loan at 7.5% over 5 years, adding $100 a month pays it off in about 49 months instead of 60 and saves roughly $1,014 in interest. Check first that your loan has no prepayment penalty.',
      },
      {
        id: 'loan-term-in-months',
        category: 'loan',
        question: 'How do I enter a loan term in months, such as 60 or 72 months?',
        answer:
          'The calculator takes the term in years, so divide the months by 12: 36 months is 3 years, 48 is 4, 60 is 5, 72 is 6 and 84 is 7. Longer terms lower the monthly payment but raise total interest.',
      },
      {
        id: 'apr-vs-interest-rate',
        category: 'loan',
        question: 'What is the difference between APR and interest rate?',
        answer:
          'The interest rate is the yearly cost of borrowing the principal. The Annual Percentage Rate (APR) also includes lender fees and origination charges, so it reflects the total cost of credit. Entering the APR gives the most realistic payment estimate.',
      },
      {
        id: 'simple-vs-amortized-interest',
        category: 'loan',
        question: 'What is the difference between simple interest and an amortized loan?',
        answer:
          'Simple interest multiplies the original principal by the rate and the number of years, which overstates the cost of a loan you repay in installments. An amortized loan charges interest only on the balance you still owe, so the interest shrinks with each payment. This calculator uses the amortized method, which is how most car and personal loans work.',
      },
      {
        id: 'biweekly-loan-payments',
        category: 'loan',
        question: 'Do biweekly payments save money on a loan?',
        answer:
          'They can. Paying half the monthly amount every two weeks adds up to 13 full payments a year instead of 12. If your lender applies each payment promptly, the extra goes to principal and shortens the loan. Ask your lender how biweekly payments are credited, as some hold them until the monthly due date.',
      },
      {
        id: 'which-loan-pay-first',
        category: 'loan',
        question: 'Which loan should I pay off first?',
        answer:
          'Pay at least the minimum on every loan, then put extra money toward the loan with the highest interest rate to minimize total interest. Another approach is to pay off the smallest balance first for momentum. Compare your loans in the calculator and check for prepayment penalties before paying extra.',
      },
      {
        id: 'loan-affordability',
        category: 'loan',
        question: 'How much loan can I afford?',
        answer:
          'Start with the monthly payment that fits your budget, then try different loan amounts in the calculator until the payment matches it. Change the term to see how a longer or shorter loan changes the amount you can borrow at that payment. Lenders also look at your income, debts and credit.',
      },
      {
        id: 'supported-loan-types',
        category: 'loan',
        question: 'Can I use this calculator for personal, auto, boat, RV, home equity or business loans?',
        answer:
          'Yes, for any fixed-rate loan with equal payments, including car, personal, boat, motorcycle, RV, equipment, land, business and fixed-rate home equity loans. It does not model balloon payments, interest-only periods, variable rates, HELOCs, or program fees such as VA, FHA or SBA charges, so treat it as a baseline and confirm the full terms with your lender.',
      },
    ],
  },
  mortgage: {
    eyebrow: 'Real Estate & Financing FAQs',
    title: 'Mortgage Calculator FAQs: Payments, Affordability & Terms',
    description:
      'Answers on how to calculate a monthly mortgage payment, how much mortgage you can afford, 15 vs 30-year terms, PITI, PMI, extra payments and refinancing.',
    ctaText: 'Calculate your estimated monthly mortgage payment, taxes, and insurance using our tool above.',
    items: [
      {
        id: 'mortgage-payment-formula',
        category: 'mortgage',
        question: 'How do I calculate a monthly mortgage payment?',
        answer:
          'Use M = P × [r(1 + r)^n] ÷ [(1 + r)^n − 1], where P is the loan amount, r is the monthly rate (annual rate ÷ 12) and n is the number of payments (years × 12). A $300,000 loan at 6.5% over 30 years comes to about $1,896.20 a month in principal and interest.',
      },
      {
        id: 'how-mortgage-calculator-works',
        category: 'mortgage',
        question: 'How does a mortgage calculator work?',
        answer:
          'A mortgage calculator takes the loan amount, interest rate and term, applies the fixed-rate amortization formula, and returns your monthly payment, total interest and total cost. Change any input to see how a different rate or term affects what you pay each month and over the life of the loan.',
      },
      {
        id: 'how-much-mortgage-afford',
        category: 'mortgage',
        question: 'How much mortgage can I afford?',
        answer:
          'A common guide is the 28/36 rule. On $8,000 of gross monthly income, housing costs should stay near $2,240 (28%) and total debt payments near $2,880 (36%). Remember your housing cost includes taxes and insurance, not just principal and interest, so leave room for them when choosing a loan amount.',
      },
      {
        id: 'what-is-piti',
        category: 'mortgage',
        question: 'What does PITI stand for in a mortgage payment?',
        answer:
          'PITI stands for Principal, Interest, Taxes, and Insurance. These four elements make up your total monthly housing obligation: Principal repays borrowed loan balance, Interest pays the lender, Property Taxes support local municipality services, and Homeowners Insurance protects the property against damage.',
      },
      {
        id: 'pmi-requirements',
        category: 'mortgage',
        question: 'What is Private Mortgage Insurance (PMI) and when is it required?',
        answer:
          'PMI is insurance that protects the lender if a borrower defaults on a conventional mortgage. It is generally required when a buyer makes a down payment of less than 20% of the home\'s purchase price, and can typically be cancelled once your equity reaches 20%.',
      },
      {
        id: '15-vs-30-year-mortgage',
        category: 'mortgage',
        question: 'What is the difference between a 15-year and 30-year mortgage?',
        answer:
          'A 30-year mortgage has lower monthly payments because loan payoff is spread across 360 months, but results in far higher total interest over the life of the loan. A 15-year mortgage requires higher monthly payments, but has lower interest rates and saves tens of thousands of dollars in overall interest.',
      },
      {
        id: 'mortgage-calculator-taxes-insurance',
        category: 'mortgage',
        question: 'Does this mortgage calculator include taxes and insurance?',
        answer:
          'No. It calculates principal and interest only. To estimate your full monthly cost, divide your annual property tax and homeowners insurance by 12 and add them, plus any PMI or HOA dues, to the result. Lenders often collect these in an escrow account alongside your payment.',
      },
      {
        id: 'mortgage-down-payment',
        category: 'mortgage',
        question: 'How does a down payment affect my mortgage payment?',
        answer:
          'A larger down payment lowers the amount you borrow, which reduces your monthly payment and total interest. Subtract the down payment from the home price and enter the result as the loan amount. Putting down 20% or more also avoids PMI on most conventional loans.',
      },
      {
        id: 'mortgage-extra-payments',
        category: 'mortgage',
        question: 'How do extra payments help pay off a mortgage early?',
        answer:
          'Extra payments go straight to principal, so interest is charged on a smaller balance for the rest of the loan. That shortens the payoff date and cuts total interest. This calculator shows the standard schedule; enter a shorter term to preview an early payoff, and confirm your lender has no prepayment penalty.',
      },
      {
        id: 'closing-costs-budget',
        category: 'mortgage',
        question: 'How much should I budget for mortgage closing costs?',
        answer:
          'Closing costs usually range between 2% and 5% of the total loan amount. These include appraisal fees, title searches, loan origination fees, home inspections, and prepaid escrow deposits.',
      },
      {
        id: 'debt-to-income-rule',
        category: 'mortgage',
        question: 'What is the 28/36 rule in mortgage qualification?',
        answer:
          'The 28/36 rule is a standard lending guideline: your monthly housing costs (PITI) should not exceed 28% of your gross monthly income, and total debt obligations (housing plus car loans, student debt, credit cards) should not exceed 36% of your gross income.',
      },
      {
        id: 'when-to-refinance-mortgage',
        category: 'mortgage',
        question: 'When should I refinance a mortgage?',
        answer:
          'Refinancing can make sense when a new rate is meaningfully lower and you will stay long enough to recover the closing costs. Divide the closing costs by your monthly savings to find the break-even point. For example, $6,000 in costs and $150 saved per month breaks even in 40 months.',
      },
      {
        id: 'mortgage-calculator-accuracy',
        category: 'mortgage',
        question: 'Are mortgage calculators accurate?',
        answer:
          'The principal and interest figure is exact for a fixed-rate loan when you enter the correct rate and term. Your real payment can differ because of property taxes, insurance, PMI, HOA dues, fees and rate changes on adjustable loans, so treat the result as an estimate and confirm it with your lender.',
      },
      {
        id: 'mortgage-loan-types-supported',
        category: 'mortgage',
        question: 'Can I use this calculator for FHA, VA, manufactured home, land or second home loans?',
        answer:
          'Yes, for the principal and interest portion of any fixed-rate loan. Program costs such as the FHA mortgage insurance premium or the VA funding fee are not included, and rates for manufactured homes, land and second homes are often higher, so enter the rate your lender quotes.',
      },
      {
        id: 'mortgage-amortization-schedule',
        category: 'mortgage',
        question: 'What is a mortgage amortization schedule?',
        answer:
          'An amortization schedule shows how each payment is split between interest and principal and how the balance falls over time. Early payments are mostly interest. For a year-by-year schedule, enter the same amount, rate and term in the Loan Calculator, which includes an annual amortization table.',
      },
    ],
  },
  password: {
    eyebrow: 'Cybersecurity & Privacy FAQs',
    title: 'Password Security & Privacy FAQs',
    description:
      'Best practices for generating high-entropy passwords, password manager security, and protecting digital accounts against credential stuffing.',
    ctaText: 'Use the password generator above to create cryptographically strong passwords with custom length and symbols.',
    items: [
      {
        id: 'pw-random-maker',
        category: 'password',
        question: 'Is this a random password maker?',
        answer:
          'Yes. It builds each password from random characters chosen in the browser, using the length and character types selected. Nothing typed or generated is sent to a server.',
      },
      {
        id: 'pw-easy-generator',
        category: 'password',
        question: 'Is there an easy random password generator that needs no account?',
        answer:
          'Yes. Pick a length, tick the character types and press generate. There is nothing to install and no sign-up step, and the password can be copied in one tap.',
      },
      {
        id: 'pw-generate-online',
        category: 'password',
        question: 'Is it safe to generate a password online?',
        answer:
          'It is safe when the page creates the password locally and does not transmit it. This generator uses the browser’s own secure random source, so the result never leaves the device. As with any password, store it in a password manager.',
      },
      {
        id: 'pw-create-random',
        category: 'password',
        question: 'How long should a random password be?',
        answer:
          'To create random password strings that are hard to guess, length matters most: 12 characters is a sensible minimum for ordinary accounts and 16 or more suits email, banking and password-manager master passwords.',
      },
      {
        id: 'cryptographic-security',
        category: 'security',
        question: 'What makes a password cryptographically secure?',
        answer:
          'A secure one has high entropy: at least 16 characters, a mix of uppercase and lowercase letters, numbers and symbols, and no dictionary words, personal details or predictable keyboard patterns.',
      },
      {
        id: 'length-vs-complexity',
        category: 'security',
        question: 'Why is password length more important than complexity alone?',
        answer:
          'Every extra character multiplies the number of guesses an attacker needs. An 8-character password with mixed characters can fall in hours or days to a modern GPU cluster, whereas a random 16-character one needs septillions of guesses, which would take centuries.',
      },
      {
        id: 'password-managers-safety',
        category: 'security',
        question: 'Are password managers safe to store generated passwords?',
        answer:
          'Yes, if you choose a reputable one. Good managers encrypt your vault on your device with strong ciphers such as AES-256, so even if the service\'s servers are breached, the data cannot be read without your master password and secret recovery key.',
      },
      {
        id: 'password-reuse-danger',
        category: 'security',
        question: 'Why should I never reuse passwords across multiple accounts?',
        answer:
          'When one website suffers a data breach, attackers test the leaked email and password pair against thousands of other services in automated "credential stuffing" attacks. A unique login for every account means a breach on one site cannot unlock your other accounts.',
      },
      {
        id: 'how-often-change-passwords',
        category: 'security',
        question: 'How often should I change my passwords?',
        answer:
          'Current guidance, including NIST’s, advises against routine forced changes because they push people toward predictable variations. Create long, random ones, keep them in a password manager, and change one right away only if a service reports a breach or you notice suspicious activity.',
      },
    ],
  },
};

interface FAQProps {
  tool?: ToolKey;
  items?: FAQItem[];
  className?: string;
  /** Optional per-page heading (eyebrow, title, description, CTA) layered over the tool's default. */
  heading?: FaqHeading;
}

export const FAQ: React.FC<FAQProps> = ({ tool = 'quick', items, className = '', heading }) => {
  const baseConfig = TOOL_FAQS[tool] || TOOL_FAQS.quick;
  const config: ToolFaqConfig = heading ? { ...baseConfig, ...heading } : baseConfig;
  const activeItems = items || config.items;

  // Open first two items by default
  const defaultOpen = new Set(activeItems.slice(0, 2).map((item) => item.id));
  const [openIds, setOpenIds] = useState<Set<string>>(defaultOpen);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const baseId = useId();

  // Reset open items when tool changes
  React.useEffect(() => {
    setOpenIds(new Set(activeItems.slice(0, 2).map((item) => item.id)));
    setSearchQuery('');
  }, [tool]);

  const toggleItem = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setOpenIds(new Set(filteredItems.map((item) => item.id)));
  };

  const collapseAll = () => {
    setOpenIds(new Set());
  };

  // Filter items based on search query
  const filteredItems = activeItems.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      item.question.toLowerCase().includes(query) ||
      item.answer.toLowerCase().includes(query)
    );
  });

  // Schema.org FAQPage JSON-LD structured data for rich SEO snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: activeItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  // Direct DOM synchronization to ensure document.head contains the updated FAQPage JSON-LD schema
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let scriptTag = document.querySelector('script#schema-org-faq') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('id', 'schema-org-faq');
      document.head.appendChild(scriptTag);
    } else {
      scriptTag.removeAttribute('data-rh');
    }
    scriptTag.textContent = JSON.stringify(faqSchema, null, 2);
    return () => {
      document.querySelector('script#schema-org-faq')?.remove();
    };
  }, [faqSchema]);

  return (
    <>
      {/* Schema.org FAQPage structured data injected into <head> for Google 'People Also Ask' rich snippets */}
      {typeof window === 'undefined' && (
        <Helmet>
          <script id="schema-org-faq" type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}

      <section
        className={`faq-section guide-shell ${className}`.trim()}
        aria-labelledby={`${baseId}-faq-title`}
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="guide-card font-sans">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-teal-600 font-mono mb-1">
              <HelpCircle className="w-4 h-4" aria-hidden="true" />
              <span>{config.eyebrow}</span>
            </div>
            <h2
              id={`${baseId}-faq-title`}
              className="faq-title"
            >
              {config.title}
            </h2>
            <p className="faq-desc">
              {config.description}
            </p>
          </div>

          {/* Expand / Collapse Controls */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={expandAll}
              className="px-4 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-100/90 hover:bg-slate-200/90 rounded-full transition-all border border-slate-200/60 shadow-xs cursor-pointer"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="px-4 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-100/90 hover:bg-slate-200/90 rounded-full transition-all border border-slate-200/60 shadow-xs cursor-pointer"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label="Search frequently asked questions"
            placeholder={`Search ${config.title.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-stone-50 border border-stone-200 text-stone-900 focus:bg-white focus:ring-2 focus:ring-teal-600 focus:border-teal-600 rounded-2xl pl-11 pr-4 py-3.5 shadow-inner text-base sm:text-sm placeholder-slate-400 font-medium transition-all duration-200"
          />
        </div>

        {/* Accordion List */}
        <div className="space-y-3" role="region" aria-label="Questions and Answers List">
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center bg-white/70 backdrop-blur-md rounded-2xl border border-dashed border-slate-300 ">
              <p className="text-sm text-slate-500 ">
                No questions found matching &ldquo;{searchQuery}&rdquo;. Try another search term.
              </p>
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="mt-3 px-4 py-2 text-xs font-bold text-slate-700 bg-slate-100/90 rounded-full hover:bg-slate-200 transition-all border border-slate-200/60 shadow-xs cursor-pointer"
              >
                Reset Search
              </button>
            </div>
          ) : (
            filteredItems.map((item) => {
              const isOpen = openIds.has(item.id);
              const questionId = `${baseId}-q-${item.id}`;
              const answerId = `${baseId}-a-${item.id}`;

              return (
                <div
                  key={item.id}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  className={`overflow-hidden bg-slate-50/70 border border-slate-200/80 shadow-xs rounded-2xl mb-3 transition-all hover:shadow-md ${
                    isOpen
                      ? 'ring-2 ring-teal-500/20 border-teal-500/30 '
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    id={questionId}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    onClick={() => toggleItem(item.id)}
                    className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 cursor-pointer"
                  >
                    <span
                      itemProp="name"
                      className="faq-q tracking-tight"
                    >
                      {item.question}
                    </span>
                    <span
                      className={`flex items-center justify-center w-8 h-8 rounded-full transition-transform duration-200 shrink-0 ${
                        isOpen
                          ? 'rotate-180 bg-[#2457F5] text-white shadow-xs'
                          : 'bg-slate-100 text-slate-500 '
                      }`}
                    >
                      <ChevronDown className="w-4 h-4" aria-hidden="true" />
                    </span>
                  </button>

                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className={`overflow-hidden ${isOpen ? 'block' : 'hidden'}`}
                  >
                    <div
                      itemProp="text"
                      className="faq-a px-5 pb-5 pt-3 border-t border-slate-100 "
                    >
                      {item.answer}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Helpful User Support Footer Card */}
        <div className="mt-8 p-5 sm:p-6 bg-slate-50/70 border border-slate-200/80 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2457F5] text-white flex items-center justify-center shrink-0 shadow-md shadow-[#2457F5]/20">
              <Sparkles className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 ">
                Interactive Tools Available
              </h3>
              <p className="text-xs text-slate-600 font-medium">
                {config.ctaText}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-5 py-2.5 text-xs font-bold text-slate-900 bg-slate-100/90 border border-slate-200/60 rounded-full hover:bg-slate-200/90 transition-all shrink-0 shadow-xs cursor-pointer"
          >
            Back to Top Calculator ↑
          </button>
        </div>
        </div>
      </section>
    </>
  );
};

/**
 * Returns formatted Schema.org FAQPage JSON-LD object for a specific calculator tool.
 */
export function getFaqSchema(tool: ToolKey) {
  const config = TOOL_FAQS[tool] || TOOL_FAQS.quick;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}
